import React, { ReactNode, RefObject } from 'react';
import styles from './search-bar.module.scss';
import IconInput, { IconInputProps } from 'components/atomic/molecules/icon-input';
import { buildClassName } from 'utils/build-class-name';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';

//#region Types
interface SearchBarBaseProps {
  /** Dropdown content (suggestions) */
  dropdown?: ReactNode;
  /** Whether the dropdown is visible */
  isDropdownOpen?: boolean;
  /** Handler for clicking outside the search bar */
  onClickOutside?: () => void;
  /** Additional CSS class */
  className?: string;
  /** Additional CSS class for the dropdown wrapper */
  dropdownClassName?: string;
  /** Test ID for automated testing */
  testId?: string;
}

interface SearchBarWithIconInputProps extends SearchBarBaseProps {
  /** Props for the icon input */
  inputProps: Omit<IconInputProps, 'ref'>;
  /** Ref for the input element */
  inputRef?: RefObject<HTMLInputElement>;
  renderInput?: undefined;
}

interface SearchBarWithCustomInputProps extends SearchBarBaseProps {
  inputProps?: undefined;
  inputRef?: undefined;
  /**
   * Escape hatch to render a custom input instead of the default `IconInput`.
   * Use when a consumer needs a non-MUI input while still reusing this
   * organism's outside-click + dropdown-slot behavior.
   */
  renderInput: () => ReactNode;
}

export type SearchBarProps = SearchBarWithIconInputProps | SearchBarWithCustomInputProps;
//#endregion

/**
 * SearchBar - A search bar with dropdown suggestions
 * 
 * Atomic Level: Organism
 * 
 * Features:
 * - Composable architecture with input and dropdown slots
 * - Outside click detection for dropdown
 * - Icon slots (left, middle, right)
 * - Tag support for selected areas
 * 
 * @example
 * ```tsx
 * <SearchBar
 *   inputProps={{
 *     value: query,
 *     onValueChange: setQuery,
 *     rightIcon: <SearchIcon />,
 *     middleIcon: <CloseIcon />,
 *     showMiddleIcon: query.length > 0,
 *     placeholder: "Search...",
 *   }}
 *   dropdown={isOpen && <SuggestionList items={suggestions} />}
 *   isDropdownOpen={isOpen}
 *   onClickOutside={() => setIsOpen(false)}
 * />
 * ```
 */
export default function SearchBar({
  inputProps,
  inputRef,
  dropdown,
  isDropdownOpen = false,
  onClickOutside,
  className,
  dropdownClassName,
  testId,
  renderInput,
}: SearchBarProps) {
  const wrapperRef = useOutsideClickHandler(() => {
    onClickOutside?.();
  });

  return (
    <div
      ref={wrapperRef as RefObject<HTMLDivElement>}
      className={buildClassName(styles.component, className)}
      data-testid={testId}
    >
      {isDropdownOpen && dropdown && (
        <div className={buildClassName(styles.dropdown, dropdownClassName)}>
          {dropdown}
        </div>
      )}

      {renderInput ? renderInput() : (
        <IconInput
          ref={inputRef}
          {...inputProps}
        />
      )}
    </div>
  );
}

export { SearchBar };
